<template>
  <div class="busan-page px-4 py-4 md:px-5">
    <BasicLoading v-if="app.isLoading" />

    <div class="mx-auto space-y-5">
      <!-- 안내 섹션 -->
      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p class="mb-3 text-[15px] font-semibold text-slate-900">자기주도학습 과제제출</p>
        <ul class="space-y-2 text-sm text-slate-600">
          <li class="guide-item">
            <span class="guide-dot">1</span>
            <span>학습자에게 설정된 교재 정보는 CTM의 마지막 렉쳐 정보를 기준으로 합니다.</span>
          </li>
          <li class="guide-item">
            <span class="guide-dot">2</span>
            <span>
              자기주도학습(퀴즈)는 각각의 교재와 대상별로 구성되어있으며, 다음의 교재가 아닌경우 자기주도학습을 할 수 없습니다.<br />
              - 초등(초등학생인 경우) : It's Me! - Student Book 6/8/10/12/14<br />
              - 중등(초등학생이 아닌 경우) : It's Me! - Student Book 12/14/16, 365 Think 1-1/1-2
            </span>
          </li>
          <li class="guide-item">
            <span class="guide-dot">3</span>
            <span>퀴즈에 사용되는 이미지의 크기는 652 x 436 (px) 로 사용해주세요</span>
          </li>
        </ul>
      </section>

      <!-- 필터 섹션 -->
      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex flex-col gap-4">
          <!-- 검색어 행 -->
          <div class="grid gap-4 md:grid-cols-[minmax(0,0.5fr)_minmax(0,1fr)_120px]">
            <label class="filter-field">
              <span class="filter-label">검색 기준</span>
              <select v-model="app.params.searchType" class="filter-control" @change="search">
                <option value="">선택</option>
                <option value="이름">이름</option>
                <option value="이메일">이메일</option>
                <option value="핸드폰">핸드폰</option>
              </select>
            </label>
            <label class="filter-field">
              <span class="filter-label">검색어</span>
              <input
                v-model="app.params.keyword"
                type="text"
                class="filter-control"
                placeholder="검색어 입력"
                @keyup.enter="search"
              />
            </label>
            <button type="button" class="search-button" @click="search">검색</button>
          </div>

          <!-- 추가 필터 행 -->
          <div class="grid gap-4 md:grid-cols-[minmax(0,0.55fr)_minmax(0,0.65fr)_minmax(0,0.55fr)_minmax(0,1fr)_minmax(0,0.7fr)]">
            <label class="filter-field">
              <span class="filter-label">미제출 횟수</span>
              <input type="number" v-model="app.params.round" placeholder="횟수 입력" class="filter-control" @keyup.enter="search" />
            </label>
            <label class="filter-field">
              <span class="filter-label">학교 레벨</span>
              <select v-model="app.params.schoolLevel" class="filter-control" @change="search">
                <option value="">학교레벨 선택</option>
                <option value="초등">초등</option>
                <option value="중등">중등</option>
              </select>
            </label>
            <label class="filter-field">
              <span class="filter-label">과정집 번호</span>
              <input type="number" v-model="app.params.idxB2b" placeholder="번호 입력" class="filter-control" @keyup.enter="search" />
            </label>
            <label class="filter-field">
              <span class="filter-label">교재명</span>
              <select v-model="app.params.bookName" class="filter-control" @change="search">
                <option value="">교재명 선택</option>
                <option v-for="book in app.bookList" :key="book" :value="book">{{ book }}</option>
              </select>
            </label>
            <label class="filter-field">
              <span class="filter-label">수업 상태</span>
              <div class="filter-control filter-checkbox-group">
                <span class="flex items-center gap-2">
                  <input type="checkbox" v-model="app.params.isInClass" true-value="Y" false-value="N" id="수업중" @change="search" />
                  <label for="수업중" class="cursor-pointer text-sm text-slate-700">수업중</label>
                </span>
                <span class="flex items-center gap-2">
                  <input type="checkbox" v-model="app.params.isEndClass" true-value="Y" false-value="N" id="수업완료" @change="search" />
                  <label for="수업완료" class="cursor-pointer text-sm text-slate-700">수업완료</label>
                </span>
              </div>
            </label>
          </div>

          <!-- 액션 버튼 -->
          <div class="flex flex-wrap gap-3 border-t border-slate-100 pt-4">
            <button type="button" @click="homework.setIsShowHomeworkModal" class="action-button action-blue">
              과제/해설지 일괄 업로드
            </button>
            <button type="button" @click="app.downloadStudentInfo" class="action-button action-green">
              학습자 정보 다운로드
            </button>
            <button type="button" @click="app.downloadStudyStatus" class="action-button action-red">
              학습현황 다운로드
            </button>
          </div>
        </div>
      </section>

      <!-- 테이블 섹션 -->
      <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p class="text-base font-semibold text-slate-900">학습자 목록</p>
            <p class="mt-1 text-sm text-slate-500">총 {{ app.params.totalCount > 0 ? app.params.totalCount : 0 }}건</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <select v-model="app.params.pageSize" class="filter-control" style="width: auto; min-width: 8rem; height: 2.25rem; padding: 0.35rem 0.7rem; font-size: 0.875rem;" @change="search">
              <option :value="20">20개씩 보기</option>
              <option :value="50">50개씩 보기</option>
              <option :value="100">100개씩 보기</option>
              <option :value="500">500개씩 보기</option>
              <option :value="0">전체 보기</option>
            </select>
          </div>
        </div>

        <!-- SMS 발송 버튼 -->
        <div class="mb-3 flex flex-wrap justify-end gap-2">
          <button type="button" @click="sendingMessage.setIsShowMessageModal('student')" class="action-button action-slate">
            학생 SMS 발송
          </button>
          <button type="button" @click="sendingMessage.setIsShowMessageModal('parent')" class="action-button action-slate">
            학부모 SMS 발송
          </button>
          <button type="button" @click="sendingMessage.setIsShowMessageModal('all')" class="action-button action-slate">
            학생&amp;학부모 SMS 발송
          </button>
        </div>

        <div class="table-scroll-wrap rounded-xl border border-slate-200">
          <table class="w-full border-collapse" style="table-layout: auto;">
            <thead>
              <tr>
                <th @click="sendingMessage.setAllCheck" class="cursor-pointer">
                  <input :checked="sendingMessage.checkList.length === app.questionUserList.length" type="checkbox" />
                </th>
                <th>No</th>
                <th>이름</th>
                <th>생성일</th>
                <th>상태</th>
                <th>ID</th>
                <th>학생<br />핸드폰번호</th>
                <th>학부모<br />핸드폰번호</th>
                <th>생년월일</th>
                <th>학교레벨</th>
                <th>학년</th>
                <th>과정집번호</th>
                <th>교재명</th>
                <th>제출여부</th>
                <th>SMS발송</th>
                <th>발송내역</th>
              </tr>
            </thead>
            <tbody v-if="app.questionUserList.length > 0">
              <tr v-for="questionUser in app.questionUserList" :key="questionUser.rowNum">
                <td @click="sendingMessage.setCheckList(questionUser)" class="cursor-pointer">
                  <input :checked="sendingMessage.checkList.find((item) => item.rowNum === questionUser.rowNum)" type="checkbox" />
                </td>
                <td>{{ questionUser.rowNum }}</td>
                <td>{{ questionUser.kname }}</td>
                <td>{{ questionUser.createDate }}</td>
                <td>{{ questionUser.lectureStatus }}</td>
                <td>{{ questionUser.uid }}</td>
                <td>{{ questionUser.mobile }}</td>
                <td>{{ questionUser.mobileParent }}</td>
                <td>{{ questionUser.birthday }}</td>
                <td>{{ questionUser.schoolLevel }}</td>
                <td>{{ questionUser.schoolGrade }}</td>
                <td>{{ questionUser.idxB2b || "-" }}</td>
                <td :title="questionUser.bookName">{{ questionUser.bookName }}</td>
                <td
                  @click="() => { if (questionUser.bookName) { submitModal.setIsShowSubmitStatus(); submitModal.getSubmitList(questionUser); } }"
                  :class="{ 'cursor-pointer': questionUser.bookName }"
                >
                  <span :class="{ 'underline underline-offset-2 font-semibold text-teal-700': questionUser.bookName }">
                    {{ questionUser.questionCompleteCnt }}/24
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    @click="sendingMessage.setIsShowMessageModal('all', questionUser)"
                    class="table-action-button table-action-slate"
                  >학생&amp;학부모 SMS</button>
                </td>
                <td>
                  <button
                    type="button"
                    @click="() => { sendedMessage.setIsShowSendedMessage(); sendedMessage.getMessageList(questionUser.idxCtmMember); }"
                    class="table-action-button table-action-blue"
                  >View</button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="16">조회된 결과가 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-2">
          <BagicPaging
            :totalCnt="app.params.totalCount"
            :currentPage="app.params.pageNum"
            :pageSize="app.params.pageSize"
            @paging-click="app.movePage"
          />
        </div>
      </section>
    </div>

    <!-- 제출여부 모달 -->
    <div v-if="submitModal.isShowSubmitStatus" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="submitModal.setIsShowSubmitStatus"></div>
      <div class="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 class="text-lg font-semibold text-slate-900">제출 현황</h3>
          <button type="button" class="modal-close-btn" @click="submitModal.setIsShowSubmitStatus">×</button>
        </div>
        <div class="overflow-y-auto px-6 py-5">
          <table class="w-full border-collapse">
            <thead>
              <tr>
                <th>회차</th>
                <th>제출일자/요일</th>
                <th>제출여부</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="submit in submitModal.submitList" :key="submit.round">
                <td>{{ submit.round }}회</td>
                <td>{{ submit.createDate }}</td>
                <td>
                  <span
                    :class="{ 'underline cursor-pointer': submit.isComplete !== '-' }"
                    @click="submit.isComplete !== '-' && getCheckTriedQuestion(submit.round)"
                    class="inline-block"
                  >{{ submit.isComplete }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 발송내역 모달 -->
    <div v-if="sendedMessage.isShowSendedMessage" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="sendedMessage.setIsShowSendedMessage"></div>
      <div class="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 class="text-lg font-semibold text-slate-900">발송 내역</h3>
          <button type="button" class="modal-close-btn" @click="sendedMessage.setIsShowSendedMessage">×</button>
        </div>
        <div class="max-h-[calc(90vh-74px)] overflow-y-auto px-6 py-5">
          <div class="mb-4">
            <select
              v-model="sendedMessage.params.searchDate"
              @change="sendedMessage.getMessageList()"
              style="min-width: 160px; height: 2.5rem; border: 1px solid #cbd5e1; border-radius: 0.75rem; background-color: #fff; padding: 0 2rem 0 0.75rem; font-size: 0.9rem; color: #0f172a; outline: none;"
            >
              <option :value="String(Number(sendedMessageMonths[0]) - 1)">{{ Number(sendedMessageMonths[0]) - 1 }} 이전</option>
              <option v-for="m in sendedMessageMonths" :key="m" :value="m">{{ m }}</option>
            </select>
          </div>
          <div class="flex gap-6">
            <div class="grow overflow-x-auto">
              <table class="w-full border-collapse">
                <thead>
                  <tr>
                    <th>내용</th>
                    <th>수신번호</th>
                    <th>발신자</th>
                    <th>발송일</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="sendedMessage.messageList.length === 0">
                    <td colspan="4">데이터가 없습니다.</td>
                  </tr>
                  <tr
                    v-for="message in sendedMessage.messageList"
                    :key="message.rowNum"
                    @click="sendedMessage.setSelectedMessage(message)"
                    :class="{ 'bg-slate-100': sendedMessage.selectedMessage.rowNum === message.rowNum }"
                    class="cursor-pointer hover:bg-slate-50"
                  >
                    <td class="max-w-xs"><p :title="message.contents" class="line-clamp-1">{{ message.contents }}</p></td>
                    <td>{{ message.reciveNumber }}</td>
                    <td>{{ message.staffName }}</td>
                    <td>{{ message.createDate.split(" ")[0] }}<br />{{ message.createDate.split(" ")[1] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="flex-none w-80">
              <p class="rounded-t-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white">MEMO</p>
              <p class="min-h-[200px] max-h-[400px] overflow-y-auto rounded-b-xl border border-slate-200 p-4 text-sm text-slate-700 whitespace-pre-line">
                {{ sendedMessage.selectedMessage.contents || "" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SMS 발송 모달 -->
    <div v-if="sendingMessage.isShowMessageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="sendingMessage.setIsShowMessageModal"></div>
      <div class="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 class="text-lg font-semibold text-slate-900">
            SMS 발송 ({{ sendingMessage.postInfo.type === "all" ? "학생&amp;학부모" : sendingMessage.postInfo.type === "student" ? "학생" : "학부모" }})
          </h3>
          <button type="button" class="modal-close-btn" @click="sendingMessage.setIsShowMessageModal">×</button>
        </div>
        <div class="overflow-y-auto px-6 py-5">
          <div class="mb-5 overflow-hidden rounded-xl border border-slate-200">
            <div class="flex border-b border-slate-200">
              <span class="flex-none w-24 bg-slate-800 px-4 py-3 text-sm font-semibold text-white">발신번호</span>
              <span class="px-4 py-3 text-sm text-slate-700">07074185381</span>
            </div>
            <div class="flex border-b border-slate-200">
              <span class="flex-none w-24 bg-slate-800 px-4 py-3 text-sm font-semibold text-white">수신자</span>
              <span class="px-4 py-3 text-sm text-slate-700">{{ sendingSetList }}</span>
            </div>
            <div class="flex">
              <span class="flex-none w-24 bg-slate-800 px-4 py-3 text-sm font-semibold text-white">내용</span>
              <textarea
                v-model="sendingMessage.postInfo.message"
                class="flex-1 resize-none p-3 text-sm outline-none min-h-48"
              ></textarea>
            </div>
          </div>
          <div class="flex justify-between">
            <button type="button" @click="sendingMessage.setIsShowMessageModal" class="action-button action-red">취소</button>
            <button type="button" @click="sendingMessage.postMessage" class="action-button action-blue">전송</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 과제/해설지 업로드 모달 -->
    <div v-if="homework.isShowHomeworkModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40" @click="homework.setIsShowHomeworkModal"></div>
      <div class="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 class="text-lg font-semibold text-slate-900">과제 및 해설지 일괄 업로드</h3>
          <button type="button" class="modal-close-btn" @click="homework.setIsShowHomeworkModal">×</button>
        </div>
        <div class="overflow-y-auto px-6 py-5">
          <div class="mb-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5">
            <p class="mb-3 text-xs font-bold text-slate-500">파일 선택</p>
            <input
              @change="homework.setHomeworkFile"
              type="file"
              class="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border file:border-slate-300 file:bg-white file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-100 cursor-pointer"
            />
          </div>
          <div class="flex items-center justify-between">
            <button type="button" @click="homework.downloadFormat" class="text-sm font-semibold text-blue-600 underline underline-offset-2">
              등록 양식 다운로드
            </button>
            <button type="button" @click="homework.uploadHomework" class="action-button action-blue">등록</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { onMounted, reactive, computed } from "vue";
import axios from "@/plugins/axios.js";
import BagicPaging from "@/components/BagicPaging.vue";
import BasicLoading from "@/components/BasicLoading.vue";

// 금일 기준 6개월간 202406 형태로 처리
const sendedMessageMonths = computed(() => {
  let currentDate = new Date();

  let sixMonths = [];

  let currentMonth = currentDate.getMonth() + 1;
  let currentYear = currentDate.getFullYear();

  for (let i = 0; i < 6; i++) {
    if (currentMonth < 1) {
      currentMonth = 12;
      currentYear--;
    }

    let formattedDate = `${currentYear}${currentMonth.toString().padStart(2, "0")}`;
    sixMonths.push(formattedDate);

    if (currentYear === 2024 && currentMonth === 7) {
      break;
    }

    currentMonth--;
  }
  sixMonths.sort();
  return sixMonths;
});

const sendingSetList = computed(() => {
  const parentMobileList = sendingMessage.checkList.filter((item) => item.mobileParent);
  const studentMobileList = sendingMessage.checkList.filter((item) => item.mobile);

  if (sendingMessage.checkList.length === 0) {
    return "전체발송";
  } else if (sendingMessage.checkList.length === 1 && sendingMessage.postInfo.type === "all") {
    return studentMobileList[0].mobile + (parentMobileList[0] ? ` 외 1명` : ``);
  } else if (sendingMessage.checkList.length === 1 && sendingMessage.postInfo.type === "student") {
    return sendingMessage.checkList[0].mobile;
  } else if (sendingMessage.checkList.length === 1 && sendingMessage.postInfo.type === "parent") {
    return sendingMessage.checkList[0].mobileParent;
  } else if (sendingMessage.postInfo.type === "all") {
    return sendingMessage.checkList[0].mobile + ` 외 ${studentMobileList.length + parentMobileList.length - 1}명`;
  } else if (sendingMessage.postInfo.type === "student") {
    return sendingMessage.checkList[0].mobile + ` 외 ${studentMobileList.length - 1}명`;
  } else {
    return sendingMessage.checkList[0].mobileParent + ` 외 ${parentMobileList.length - 1}명`;
  }
});

// 제출여부 모달 관련 reactive
const submitModal = reactive({
  isShowSubmitStatus: false,
  idxCtmMember: 0,
  idxBook: 0, // 교재번호 추가
  submitList: [],
  userInfo: {},
  setIsShowSubmitStatus: () => {
    submitModal.isShowSubmitStatus = !submitModal.isShowSubmitStatus;
    submitModal.submitList = [];
  },
  getSubmitList: async (userInfo) => {
    submitModal.idxCtmMember = userInfo.idxCtmMember;
    submitModal.idxBook = userInfo.idxBook; // 교재번호 저장
    submitModal.userInfo = userInfo;
    // API 경로 변경: idxBook 파라미터 추가
    const result = await axios.get(`/admin/question/complete/${userInfo.idxCtmMember}/${userInfo.idxBook}`);

    const { message, success, data } = result.data;

    if (!success) {
      alert(message);
      submitModal.setIsShowSubmitStatus();
      return;
    }

    submitModal.submitList = data;
  },
});

// 메시지 보내기 modal 관련 form
const sendingMessage = reactive({
  isShowMessageModal: false,
  postInfo: {
    message: `[#학습자명]님, 안녕하세요! 
부산교육청 NALDA화상영어 자기주도학습 과제를 미제출 하셨습니다.
미제출 과제를 확인 후 꼭! 제출해주세요.

※ 제출방법 : 부산교육청 NALDA홈페이지(https://www.naldapen.com) 접속 > 로그인 > 자기주도학습 > 문제풀기

※ 채점하기 버튼까지 꼭 눌러주셔야 제출 완료됩니다.

감사합니다.
`,
    type: "all",
  },
  checkList: [],
  postMessage: async () => {
    if (!sendingMessage.postInfo.message) {
      alert("메세지를 입력해주세요.");
      return;
    }
    const params = { ...sendingMessage.postInfo, idxCtmMemberList: sendingMessage.checkList.map((item) => item.idxCtmMember) };

    const result = await axios.post(`/admin/question/sms`, params);

    const { success, message } = result.data;

    alert(message);

    if (!success) return;

    sendingMessage.setIsShowMessageModal("all");
  },
  setIsShowMessageModal: (status, value) => {
    sendingMessage.postInfo.type = status;
    if (value) {
      sendingMessage.checkList = [value];
    }

    if (!sendingMessage.isShowMessageModal && sendingMessage.checkList.length === 0) {
      const isConfirm = confirm("전체를 대상으로 발송됩니다. 발송하시겠습니까?");
      if (!isConfirm) return;
    }

    sendingMessage.isShowMessageModal = !sendingMessage.isShowMessageModal;
  },
  setCheckList: (value) => {
    const findCheckList = sendingMessage.checkList.findIndex((item) => item.rowNum === value.rowNum);
    if (findCheckList === -1) {
      sendingMessage.checkList.push(value);
    } else {
      sendingMessage.checkList.splice(findCheckList, 1);
    }
  },
  setAllCheck: () => {
    if (sendingMessage.checkList.length === app.questionUserList.length) {
      sendingMessage.checkList = [];
    } else {
      sendingMessage.checkList = JSON.parse(JSON.stringify(app.questionUserList));
    }
  },
});

// 발송내역 reactive
const sendedMessage = reactive({
  isShowSendedMessage: false,
  idxCtmMember: -1,
  params: {
    searchDate: new Date().getFullYear() + (new Date().getMonth() + 1).toString().padStart(2, "0"),
  },
  messageList: [],
  selectedMessage: {},
  setSelectedMessage: (selectedMessage) => {
    sendedMessage.selectedMessage = selectedMessage;
  },
  setIsShowSendedMessage: () => {
    sendedMessage.selectedMessage = {
      contents: "",
      createDate: "",
      reciveNumber: "",
      rowNum: 0,
      sendNumber: "",
      staffName: "",
    };
    sendedMessage.isShowSendedMessage = !sendedMessage.isShowSendedMessage;
  },
  getMessageList: async (idxCtmMember) => {
    if (idxCtmMember) {
      sendedMessage.idxCtmMember = idxCtmMember;
    }

    const result = await axios.get(`/admin/question/sms/${sendedMessage.idxCtmMember}`, { params: sendedMessage.params });

    const { message, data, success } = result.data;

    if (!success) {
      alert(message);
    }
    sendedMessage.messageList = data;
  },
});

// 기본 table 및 각종 download 등 form
const app = reactive({
  isLoading: false,
  params: {
    searchType: "",
    keyword: "",
    subject: "",
    schoolLevel: "",
    bookName: "",
    idxB2b: "", // 과정집번호 (변경: isNew, isBook 제거)
    isInClass: "Y",
    isEndClass: "Y",
    round: "",
    pageSize: 20,
    pageNum: 1,
    totalCount: -1,
  },
  questionUserList: [],
  bookList: [],
  movePage(value) {
    app.params.pageNum = value;
    sendingMessage.checkList = [];
    app.getQuestionUserList();
  },
  getBookList: async () => {
    const result = await axios.get("/admin/question/book");
    const { success, data, message } = result.data;
    if (!success) {
      alert(message);
      return;
    }
    app.bookList = data;
  },
  getQuestionUserList: async () => {
    app.isLoading = true;
    const result = await axios.get("/admin/question", {
      params: app.params,
    });

    const { success, data, message } = result.data;
    app.isLoading = false;

    if (!success) {
      alert(message);
      return;
    }

    app.params.totalCount = data.totalCnt;
    app.questionUserList = data.contentList;
  },
  downloadStudentInfo: async () => {
    // 파라미터 변경: isNew, isBook 제거되고 idxB2b 사용
    const result = await axios.get("admin/question/member/excel", { params: app.params, responseType: "blob" });

    const blob = new Blob([result.data]);
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "학습자정보.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  },
  downloadStudyStatus: async () => {
    // idxB2b 파라미터 추가 (필수)
    if (!app.params.idxB2b) {
      alert("과정집번호를 입력해주세요.");
      return;
    }
    const result = await axios.get("/admin/question/learn/excel", {
      params: { idxB2b: app.params.idxB2b },
      responseType: "blob",
    });

    const blob = new Blob([result.data]);
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "학습현황.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  },
});

// 기본 과제/해설지 업로드 modal
const homework = reactive({
  file: null,
  isShowHomeworkModal: false,
  setIsShowHomeworkModal: () => {
    homework.isShowHomeworkModal = !homework.isShowHomeworkModal;
  },
  setHomeworkFile: (event) => {
    homework.file = event.target.files[0];
  },
  downloadFormat: async () => {
    window.open(
      "https://kr.object.ncloudstorage.com/lifeedu/busanExcelFile/%ED%8F%89%EC%83%9D%EA%B5%90%EC%9C%A1%ED%94%8C%EB%9E%AB%ED%8F%BC_%EB%B6%80%EC%82%B0%EA%B4%91%EC%97%AD%EC%8B%9C%EA%B5%90%EC%9C%A1%EC%B2%AD_%ED%99%94%EC%83%81%EC%98%81%EC%96%B4_%EC%97%91%EC%85%80_%EC%96%91%EC%8B%9D.xlsx"
    );
  },
  uploadHomework: async () => {
    const formData = new FormData();
    formData.append("file", homework.file);

    const result = await axios.post("/admin/question/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const { success, message } = result.data;

    alert(message);
    if (!success) return;

    homework.setIsShowHomeworkModal();
  },
});

// 문제 열기 (변경: idxBook 파라미터 추가)
const getCheckTriedQuestion = async (round) => {
  // API 경로 변경: idxBook 파라미터 추가
  const result = await axios.get(`/admin/question/check/${submitModal.idxCtmMember}/${submitModal.idxBook}/${round}`);
  const { success, message, data } = result.data;

  if (!success) {
    alert(message);
    return;
  }
  // URL에 idxBook 파라미터 추가
  window.open(
    `${window.location.origin}/questionPage?round=${round}&check=${data}&idxCtmMember=${submitModal.idxCtmMember}&idxBook=${submitModal.idxBook}&userName=${submitModal.userInfo.kname}`,
    "_blank",
    "width=800, height=896, resizable=no, menubar=no, toolbar=no, location=no, status=no"
  );
};
const search = () => {
  app.params.pageNum = 1;
  sendingMessage.checkList = [];
  app.getQuestionUserList();
};

const init = () => {
  app.getQuestionUserList();
  app.getBookList();
};
onMounted(() => {
  init();
});
</script>
<style scoped>
.busan-page {
  background: #f8fafc;
}

.guide-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  line-height: 1.7;
}

.guide-dot {
  display: inline-flex;
  height: 1.5rem;
  width: 1.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #334155;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.filter-control {
  height: 2.875rem;
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 0.75rem;
  background-color: #fff;
  padding: 0.7rem 0.9rem;
  font-size: 0.95rem;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.filter-control:focus {
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.15);
}

.filter-checkbox-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 0.9rem;
}

.search-button {
  margin-top: auto;
  display: inline-flex;
  height: 2.875rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #2563eb;
  border-radius: 0.75rem;
  background: #fff;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2563eb;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.search-button:hover {
  background: #f8fafc;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid currentColor;
  background: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.action-button:hover {
  background: #f8fafc;
}

.action-blue  { color: #1d4ed8; }
.action-green { color: #059669; }
.action-red   { color: #dc2626; }
.action-slate { color: #334155; }

.table-scroll-wrap {
  max-height: calc(100vh - 260px);
  overflow: auto;
}

table {
  margin-bottom: 0;
  table-layout: auto;
}

thead tr {
  background: #f8fafc;
}

th {
  border-color: #e2e8f0;
  border: 1px solid #e2e8f0;
  padding: 0.85rem 0.65rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  text-align: center;
}

td {
  border: 1px solid #e2e8f0;
  padding: 0.85rem 0.65rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
  background-color: #fff;
  white-space: nowrap;
  text-align: center;
}

tbody tr:nth-child(even) td {
  background-color: #fbfdff;
}

tbody tr:hover td {
  background-color: #f8fbff;
}

.table-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4rem;
  min-height: 2.125rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid currentColor;
  border-radius: 0.75rem;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease;
}

.table-action-button:hover {
  background: #f8fafc;
}

.table-action-blue  { color: #1d4ed8; }
.table-action-slate { color: #334155; }

.modal-close-btn {
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: #f1f5f9;
  font-size: 1.125rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s ease;
}

.modal-close-btn:hover {
  background: #e2e8f0;
}


:deep(.bg-white.border-t.border-gray-200) {
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

:deep(nav[aria-label="Pagination"]) {
  gap: 0.25rem;
}

:deep(nav[aria-label="Pagination"] > span),
:deep(nav[aria-label="Pagination"] .relative.inline-flex) {
  border-radius: 14px;
}

:deep(nav[aria-label="Pagination"] .z-10) {
  background: #1d4ed8;
  color: #fff;
}
</style>
